import { Button } from 'react-scale/components';
import Image from 'next/image';
import { useState } from 'react';
import BlackBox from 'react-scale/components/elements/black-box';
import cn from 'classnames';
import SyntaxHighlighter from 'react-syntax-highlighter';
import Highlight from 'react-scale/components/elements/highlight';
import Json from 'react-scale/components/pages/document/industries-section/ExampleContent/Json';

import s from './attribute-enrichment.module.scss';

const AttributeEnrichmentExample = () => {
  const [state, setState] = useState<'idle' | 'loading' | 'completed'>('idle');

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 relative">
      <div className="sm:rounded-xl bg-codGray flex items-start p-7 w-screen sm:w-auto relative left-1/2 -translate-x-1/2">
        <BlackBox className="lg:w-72 w-full mx-auto">
          <b className="text-xs mb-3 inline-block">White Party Dress</b>
          <Image
            className="rounded-lg"
            src="/static/images/example/white-dress.png"
            alt="A person wearing a white dress"
            layout="responsive"
            quality={100}
            width={252}
            height={279}
          />
        </BlackBox>
      </div>

      <div
        className={cn(
          s['code-wrapper'],
          'sm:rounded-xl bg-codGray w-screen sm:w-auto relative left-1/2 -translate-x-1/2'
        )}
      >
        <SyntaxHighlighter language="bash" style={styles}>
          {
            'curl \n --request POST \n --url https://api.scale.com/v1/catalog/extract_attributes \n --header \'accept: application/json\' \n --header \'authorization: Basic dGVzdF85NmUzYjllMWE1NzM0NDdhOGRlZjI2ZjBkOGNmNWI0Yzo=\' \n --header \'content-type: application/json\' \n --data \' { \n \t "product_name": "White Dress", \n \t "product_description": "Get your hands on this beautiful dress in time for Spring!", \n \t "primary_image_url": "sampleDress.jpg" \n }'
          }
        </SyntaxHighlighter>

        {state === 'completed' && (
          <Json
            response={
              <div className="data-collection">
                <Highlight className="javascript" key="highlight-json">
                  {response}
                </Highlight>
              </div>
            }
            responseDelay={0.1}
          />
        )}

        <div
          className={cn(
            'transition-opacity duration-150 absolute bottom-7 lg:bottom-7 right-7',
            state === 'completed' ? 'opacity-0 pointer-events-none' : 'opacity-100'
          )}
        >
          {/* @ts-ignore: Unreachable code error */}
          <Button
            disabled={state !== 'idle'}
            text={state === 'idle' ? 'EXTRACT' : 'EXTRACTING...'}
            onClick={() => {
              setState('loading');
              setTimeout(() => setState('completed'), 1000);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AttributeEnrichmentExample;

const styles = {
  hljs: {
    display: 'block',
    overflow: 'auto',
    margin: '0',
    fontSize: '12px',
    lineHeight: 2,
    background: 'transparent',
    color: '#D1AAD7',
    top: '0',
    left: '0',
    bottom: '0',
    right: '0'
  },
  'hljs-comment': {
    color: '#666'
  },
  'hljs-quote': {
    color: '#800'
  },
  'hljs-keyword': {
    color: '#008'
  },
  'hljs-selector-tag': {
    color: '#008'
  },
  'hljs-section': {
    color: '#008'
  },
  'hljs-title': {
    color: '#606'
  },
  'hljs-name': {
    color: '#008'
  },
  'hljs-variable': {
    color: '#660'
  },
  'hljs-template-variable': {
    color: '#660'
  },
  'hljs-string': {
    color: '#BBDEF2'
  },
  'hljs-selector-attr': {
    color: '#080'
  },
  'hljs-selector-pseudo': {
    color: '#080'
  },
  'hljs-regexp': {
    color: '#080'
  },
  'hljs-literal': {
    color: '#066'
  },
  'hljs-symbol': {
    color: '#066'
  },
  'hljs-bullet': {
    color: '#066'
  },
  'hljs-meta': {
    color: '#066'
  },
  'hljs-number': {
    color: '#066'
  },
  'hljs-link': {
    color: '#066'
  },
  'hljs-doctag': {
    color: '#606',
    fontWeight: 'bold'
  },
  'hljs-type': {
    color: '#606'
  },
  'hljs-attr': {
    color: '#D1AAD7'
  },
  'hljs-built_in': {
    color: '#606'
  },
  'hljs-builtin-name': {
    color: '#606'
  },
  'hljs-params': {
    color: '#606'
  },
  'hljs-attribute': {
    color: '#000'
  },
  'hljs-subst': {
    color: '#000'
  },
  'hljs-formula': {
    backgroundColor: '#eee',
    fontStyle: 'italic'
  },
  'hljs-selector-id': {
    color: '#9B703F'
  },
  'hljs-selector-class': {
    color: '#9B703F'
  },
  'hljs-addition': {
    backgroundColor: '#baeeba'
  },
  'hljs-deletion': {
    backgroundColor: '#ffc8bd'
  },
  'hljs-strong': {
    fontWeight: 'bold'
  },
  'hljs-emphasis': {
    fontStyle: 'italic'
  }
};

const response = `{
  "attributes": {
     "season": {
        "values": [
           "Spring"
        ]
     },
     "dress_style": {
        "values": [
           "Formal Dress"
        ]
     },
     "pattern": {
        "values":[
           "floral"
        ]
     },
     "sleeve_style": {
        "values": [
           "Sleeveless"
        ]
     }
  }
}`;
